import React, { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import ReactConfetti from "react-confetti";
import {
  FormBaseType,
  FormBasicsDto,
  FormSectionDto,
} from "@green0meter/api-client";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useCreateSubmission, useGetAvailableForms } from "@api";
import { MultistepForm, WelcomeBanner } from "@components/common";
import { Box } from "@components/base";
import { useUserStore } from "@store/user";
import { MultistepFormSkeleton } from "@components/common/MultistepForm";

interface Form {
  formBaseType: FormBaseType;
}

const Form: FC<Form> = ({ formBaseType }) => {
  const { formId } = useParams<{
    formId: string;
  }>();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formBasics, setFormBasics] = useState<FormBasicsDto | false>();

  const getRedirectUrl = (): string => {
    if (formBaseType === FormBaseType.VSME) {
      return "/vsme";
    }

    if (formBaseType === FormBaseType.ESRS) {
      return "/csrd";
    }

    return "/";
  };

  const { t } = useTranslation();

  const { user } = useUserStore();

  const {
    createSubmissionAsync,
    createSubmissionEnabled,
    isPendingCreateSubmission,
    createSubmissionError,
  } = useCreateSubmission(user);

  const { availableForms, isFetchingAvailableForms } = useGetAvailableForms(user, [
    formBaseType,
  ]);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const handleSubmit = async (
    values: FieldValues,
    sections: FormSectionDto[],
  ): Promise<void> => {
    const isValid = sections.every((section) =>
      section.formQuestions?.every((question) => {
        if (question.isRequired) {
          return values[question.code] !== undefined && values[question.code] !== "";
        }

        return true;
      }),
    );

    if (formId && isValid && createSubmissionEnabled) {
      await createSubmissionAsync({
        formId,
      });
      setFormSubmitted(true);
    }
  };

  useEffect(() => {
    const errorData = (createSubmissionError as AxiosError)?.response?.data as {
      message: string;
    };

    if (errorData) {
      enqueueSnackbar(errorData.message, {
        variant: "error",
      });
    }
  }, [createSubmissionError]);

  useEffect(() => {
    setFormBasics(availableForms?.find((form) => form.id === formId) ?? false);
  }, [availableForms]);

  return formSubmitted ? (
    <Box>
      <WelcomeBanner
        title={t("common.thankYou")}
        text={t("common.dataSubmittedAndBeingProcessed")}
        onClose={() => navigate(getRedirectUrl())}
        color="blue"
        icon="data-cloud"
      />
      <ReactConfetti recycle={false} />
    </Box>
  ) : formId && (isFetchingAvailableForms || !formBasics) ? (
    <MultistepFormSkeleton />
  ) : formBasics ? (
    <MultistepForm
      formSettings={{
        formBasics,
        returnUrl: getRedirectUrl(),
        button: createSubmissionEnabled
          ? {
              label: t("form.submit", "Submit"),
              onClick: (): void => {},
              popoverButtons: [
                {
                  title: t("form.submitCalculate.title"),
                  description: t("form.submitCalculate.description"),
                  onClick: handleSubmit,
                },
                {
                  title: t("form.submitCreateReports.title"),
                  description: t("form.submitCreateReports.description"),
                  onClick: handleSubmit,
                },
              ],
            }
          : undefined,
      }}
      isSubmissionPending={isPendingCreateSubmission}
    />
  ) : (
    // TODO: Create form not found page
    "Form not found"
  );
};

export default Form;
